// The notification center's merged feed — the `notification_feed` view (mig 0284)
// unions `social_notifications` (person-to-person) with `user_notifications`
// (notices from the app). Pure helpers: row mapping, the keyset cursor, the merge
// order and the tabs. The store does the I/O.

import type { AppNotification, CoinGift, NotificationFeedItem, ReleaseNotice, SocialNotification } from '../types';

/** Rows per page of the merged feed. */
export const NOTIFICATION_PAGE_SIZE = 30;

/** Unread ids fetched for the badge — far above any real backlog (max 30 rows/user, Oct 2026). */
export const UNREAD_FETCH_LIMIT = 500;

/** The columns the center reads from `notification_feed`. */
export const FEED_COLUMNS =
  'source, id, kind, created_at, read, title, body, link, ref_id, sender_id, post_id, comment_id, item_type, item_id';

export interface FeedRow {
  /** 'team' = team_messages (P3): read state lives in team_message_reads, not on the row. */
  source: 'social' | 'app' | 'team';
  id: string;
  kind: string;
  created_at: string;
  read: boolean | null;
  title: string | null;
  body: string | null;
  link: string | null;
  ref_id: string | null;
  sender_id: string | null;
  post_id: string | null;
  comment_id: string | null;
  item_type: string | null;
  item_id: string | null;
}

export interface SenderProfile {
  id: string;
  name: string | null;
  avatar_url: string | null;
}

/** Where the next page starts: the last row loaded (created_at, then id, both descending). */
export interface FeedCursor {
  createdAt: string;
  id: string;
}

export function socialFromRow(
  row: Pick<FeedRow, 'id' | 'kind' | 'created_at' | 'read' | 'sender_id' | 'post_id' | 'comment_id' | 'item_type' | 'item_id'>,
  sender: SenderProfile | undefined,
): SocialNotification {
  return {
    id: row.id,
    type: row.kind as SocialNotification['type'],
    sender: {
      id: sender?.id || row.sender_id || '',
      name: sender?.name || 'Unknown User',
      avatar: sender?.avatar_url || null,
    },
    postId: row.post_id || undefined,
    commentId: row.comment_id || undefined,
    itemType: (row.item_type || undefined) as SocialNotification['itemType'],
    itemId: row.item_id || undefined,
    createdAt: row.created_at,
    read: !!row.read,
  };
}

export function appFromRow(
  row: Pick<FeedRow, 'id' | 'kind' | 'created_at' | 'title' | 'body' | 'link' | 'ref_id'> & { read?: boolean | null; read_at?: string | null },
): AppNotification {
  return {
    id: row.id,
    kind: row.kind as AppNotification['kind'],
    title: row.title || 'Notification',
    body: row.body ?? null,
    link: row.link ?? null,
    refId: row.ref_id ?? null,
    createdAt: row.created_at,
    // the view says `read`; a realtime row from the table says `read_at`
    read: row.read != null ? !!row.read : row.read_at != null,
  };
}

/**
 * PostgREST `or` filter for "strictly older than the cursor" in (created_at desc,
 * id desc) order. ⚠️ The timestamp carries `+` and `:` — it must be double-quoted
 * inside `or()`, or PostgREST splits it.
 */
export function olderThanCursor(cursor: FeedCursor): string {
  return `created_at.lt."${cursor.createdAt}",and(created_at.eq."${cursor.createdAt}",id.lt.${cursor.id})`;
}

function newerFirst(a: { createdAt: string; id: string }, b: { createdAt: string; id: string }): number {
  const ta = Date.parse(a.createdAt);
  const tb = Date.parse(b.createdAt);
  if (ta !== tb) return tb - ta;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/** Every source in one list, newest first (the view's order). */
export function mergeFeed(
  social: SocialNotification[],
  app: AppNotification[],
  releases: ReleaseNotice[] = [],
  gifts: CoinGift[] = [],
): NotificationFeedItem[] {
  const items: NotificationFeedItem[] = [
    ...social.map((item) => ({ source: 'social' as const, item })),
    ...app.map((item) => ({ source: 'app' as const, item })),
    ...releases.map((item) => ({ source: 'release' as const, item })),
    ...gifts.map((item) => ({ source: 'gift' as const, item })),
  ];
  return items.sort((a, b) => newerFirst(a.item, b.item));
}

// ── Tabs ────────────────────────────────────────────────────────────────────
// ⭐ Tabs APPEAR AS THEY FILL (owner, 2026-10-04): All · Updates · Team · Gifts ·
// Social — every phase of the expansion has now landed its tab.
// "Team", not "Messages" — direct messages are Social, and Settings →
// Notifications already has a "Messages" category meaning DMs.

export type NotificationTab = 'all' | 'updates' | 'team' | 'gifts' | 'social';

/** App kinds that come from the team. */
const TEAM_KINDS: ReadonlySet<AppNotification['kind']> = new Set(['support_reply', 'team_message']);

export const NOTIFICATION_TABS: { id: NotificationTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'updates', label: 'Updates' },
  { id: 'team', label: 'Team' },
  { id: 'gifts', label: 'Gifts' },
  { id: 'social', label: 'Social' },
];

export function inTab(entry: NotificationFeedItem, tab: NotificationTab): boolean {
  if (tab === 'all') return true;
  if (tab === 'social') return entry.source === 'social';
  if (tab === 'updates') return entry.source === 'release';
  if (tab === 'gifts') return entry.source === 'gift';
  return entry.source === 'app' && TEAM_KINDS.has(entry.item.kind);
}

/** Copy for an empty tab. */
export const EMPTY_TAB_COPY: Record<NotificationTab, string> = {
  all: 'Nothing yet — replies from the team, likes, follows and messages land here.',
  updates: 'No updates yet — what changes in each new version of Metanoia lands here.',
  gifts: 'No gifts yet — Coin the team sends you waits here until you accept it.',
  team: 'Nothing from the team yet — messages from us and replies to your support requests land here.',
  social: 'Nothing yet — likes, replies, follows and messages land here.',
};

// ── Links ───────────────────────────────────────────────────────────────────

/** Where an app notice's link goes, as a settings section + sub-section. */
export interface SettingsTarget {
  section: string;
  sub?: string;
}

/** The `link` keys an app notice may carry → their settings destination. */
const SETTINGS_LINKS: Record<string, SettingsTarget> = {
  'settings:support': { section: 'help-support', sub: 'support' },
};

export function settingsTargetForLink(link: string | null): SettingsTarget | null {
  return link ? SETTINGS_LINKS[link] ?? null : null;
}

/** The button on an app notice's detail view, and where it goes. */
export interface NoticeAction {
  label: string;
  target: SettingsTarget;
  /** The support ticket to expand there, if any. */
  ticketId: string | null;
}

export function noticeAction(notice: Pick<AppNotification, 'kind' | 'link' | 'refId'>): NoticeAction | null {
  // replies to a team message go through support (owner, 2026-10-04): a NEW
  // request, so no ticket to expand
  if (notice.kind === 'team_message') {
    return { label: 'Reply to the team', target: SETTINGS_LINKS['settings:support'], ticketId: null };
  }
  const target = settingsTargetForLink(notice.link);
  if (!target) return null;
  if (notice.link === 'settings:support') return { label: 'Open the conversation', target, ticketId: notice.refId };
  return null;
}
